import { useTranslation } from "react-i18next";
import { Card } from "@/design-system";
import { useMyCredits } from "@/api/queries";
import { useAuth } from "@/lib/auth";
import { formatLongDate } from "@/lib/format";
import { formatCreditAmount } from "@/lib/billing";

/**
 * MembershipActivity — the sanitized class-credit activity ledger in
 * its own surface, rendered right after the membership card (issue
 * #283). Only the student projection reaches the UI: entries expose
 * just the signed `amount` and `inserted_at`; kind, note and author
 * never do. Renders nothing while there is no activity.
 */
export function MembershipActivity() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const creditsQuery = useMyCredits(token);
  const activity = creditsQuery.data?.entries ?? [];

  if (activity.length === 0) return null;

  return (
    <Card data-testid="membership-activity" padding="var(--space-4)">
      <div
        style={{
          font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
          letterSpacing: "var(--ls-wide)",
          textTransform: "uppercase",
          color: "var(--text-faint)",
          marginBottom: 8,
        }}
      >
        {t("profile.activityHeading")}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {activity.map((entry, index) => (
          <div
            key={`${entry.inserted_at}-${index}`}
            data-testid={`membership-activity-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <span
              style={{
                font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                color: "var(--text-muted)",
              }}
            >
              {formatLongDate(entry.inserted_at)}
            </span>
            <span
              style={{
                font: "var(--fw-bold) var(--fs-sm)/1 var(--font-mono)",
                color:
                  entry.amount > 0
                    ? "var(--status-success-fg)"
                    : "var(--text-strong)",
              }}
            >
              {formatCreditAmount(entry.amount)}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}